import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Button, RadioField, Table } from 'src/shared/ui/elements';

import { CheckboxField } from 'src/shared/ui/elements/index';

import { currency } from 'src/shared/utils/currency';
import { Link } from 'react-router-dom';

const OrderTableRow = ({
  $item: order,
  $handleSelectionChange,
  $hideCheckbox = false,
}) => (
  <tr>
    {!$hideCheckbox && (
      <td>
        <CheckboxField
          name={`order-${order.id}-selection`}
          $label=""
          labelClassName="fs-14 fw-500"
          checked={order.selected}
          onChange={() => $handleSelectionChange(order.id)}
        />
      </td>
    )}
    <td>
      {DateTime.cast(order.billDate || order.created)?.toFormat('MM/dd/yyyy')}
    </td>
    <td>{currency(order.price)}</td>
    <td>{currency(order.balance)}</td>
    <td>
      <Link to={`/order/${order?.orderHash}`} target="_blank">
        {order?.id}
      </Link>
    </td>
  </tr>
);

const OrderTableMobile = ({
  $item: order,
  $handleSelectionChange,
  $hideCheckbox = false,
}) => (
  <li className="pa-16 shadow-2">
    <div className="flex items-center justify-between">
      {!$hideCheckbox && (
        <CheckboxField
          name={`order-${order.id}-selection`}
          $label=""
          labelClassName="fs-14 fw-500"
          checked={order.selected}
          onChange={() => $handleSelectionChange(order.id)}
        />
      )}
      <span>Order ID: {order.id}</span>
    </div>
    <div className="mt-8">
      <p>
        Invoice Date:{' '}
        {DateTime.cast(order.billDate || order.created)?.toFormat('MM/dd/yyyy')}
      </p>
      <p>Price: {currency(order.price)}</p>
      <p>Balance: {currency(order.balance)}</p>
    </div>
  </li>
);

export const MiniOrderTable = ({
  orders,
  selectedOrderIds,
  setSelectedOrderIds,
  tableName,
  title,
  hideCheckbox = false,
}) => {
  const handleSelectionChange = (orderId) => {
    setSelectedOrderIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(orderId)) {
        return prevSelectedIds.filter((id) => id !== orderId);
      }
      return [...prevSelectedIds, orderId];
    });
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;
    if (checked) {
      setSelectedOrderIds(orders.map((order) => order.id));
    } else {
      setSelectedOrderIds([]);
    }
  };

  const ordersWithSelection = orders.map((order) => ({
    ...order,
    selected: selectedOrderIds.includes(order.id) || false,
  }));

  return (
    <div className="py-6">
      <Table
        $caption={title}
        $headers={['Invoice Date', 'Price', 'Balance', 'Order ID']}
        $items={ordersWithSelection}
        $desktopItem={OrderTableRow}
        $mobileItem={OrderTableMobile}
        $isHeaderSelectable={!hideCheckbox}
        $isSelectionHeaderSelected={
          orders.length > 0 &&
          selectedOrderIds.length === orders.length &&
          !hideCheckbox
        }
        $maxItems={hideCheckbox ? 5 : orders.length}
        $extraProps={{
          $handleSelectionChange: handleSelectionChange,
          $hideCheckbox: hideCheckbox,
        }}
        $onSelectionHeaderChange={handleSelectAll}
        $selectionHeaderName={`order-selection-${tableName}`}
      />
    </div>
  );
};

const TotalOrdersSelection = ({
  openOrders,
  totalBalanceType,
  totalBalance,
  handleTotalBalanceTypeChange,
  setSelectedOrderIds,
  selectedOrderIds,
}) => {
  const [isDueAndPendingExpanded, setIsDueAndPendingExpanded] = useState(false);
  const [isDueExpanded, setIsDueExpanded] = useState(false);

  const toggleExpand = (type) => {
    if (type === 'dueAndPending') {
      setIsDueAndPendingExpanded(!isDueAndPendingExpanded);
    } else if (type === 'due') {
      setIsDueExpanded(!isDueExpanded);
    }
  };

  const {
    pendingOrdersCount,
    activeOrdersCount,
    activeOrdersBalance,
    pendingOrdersBalance,
    activeOrders,
    pendingOrders,
    totalCount,
  } = openOrders || {};
  const hasPendingOrders = pendingOrdersCount > 0;
  const hasOpenOrders = activeOrdersCount > 0;
  useEffect(() => {
    if (totalBalanceType !== 'dueAndPending' && isDueAndPendingExpanded) {
      setIsDueAndPendingExpanded(false);
    }
    if (totalBalanceType !== 'due' && isDueExpanded) {
      setIsDueExpanded(false);
    }
  }, [isDueAndPendingExpanded, isDueExpanded, totalBalanceType]);
  const selectedOrdersCount = selectedOrderIds.length;

  if (!hasPendingOrders && !hasOpenOrders) return null;

  return (
    <>
      {hasOpenOrders && (
        <>
          <div className="amount-row">
            <RadioField
              name="totalBalanceType"
              value="due"
              $label={`Due Order${totalCount !== 1 ? 's' : ''} (${
                totalBalanceType === 'due'
                  ? selectedOrdersCount
                  : activeOrdersCount
              })`}
              checked={totalBalanceType === 'due'}
              onChange={handleTotalBalanceTypeChange('due')}
            />
            <div className="flex gapc-14">
              <p>
                {currency(
                  totalBalanceType === 'due'
                    ? totalBalance
                    : activeOrdersBalance,
                )}
              </p>
              <Button
                className="text"
                onClick={() => {
                  toggleExpand('due');
                  handleTotalBalanceTypeChange('due')();
                }}
              >
                {isDueExpanded ? 'Hide' : 'View'} Orders
              </Button>
            </div>
          </div>
          {isDueExpanded && (
            <MiniOrderTable
              orders={activeOrders}
              selectedOrderIds={
                totalBalanceType === 'due' ? selectedOrderIds : []
              }
              setSelectedOrderIds={setSelectedOrderIds}
              title="Due Orders"
              tableName="dueOrders"
            />
          )}
        </>
      )}
      {hasPendingOrders && (
        <>
          <div className="amount-row">
            <RadioField
              name="totalBalanceType"
              value="dueAndPending"
              $label={`${
                hasPendingOrders && hasOpenOrders
                  ? 'Due & Upcoming'
                  : 'Upcoming'
              } Order${totalCount !== 1 ? 's' : ''} (${
                totalBalanceType === 'dueAndPending'
                  ? selectedOrdersCount
                  : totalCount
              })`}
              checked={totalBalanceType === 'dueAndPending'}
              onChange={handleTotalBalanceTypeChange('dueAndPending')}
            />
            <div className="flex gapc-14">
              <p>
                {currency(
                  totalBalanceType === 'dueAndPending'
                    ? totalBalance
                    : activeOrdersBalance + pendingOrdersBalance,
                )}
              </p>
              <Button
                className="text"
                onClick={() => {
                  toggleExpand('dueAndPending');
                  handleTotalBalanceTypeChange('dueAndPending')();
                }}
              >
                {isDueAndPendingExpanded ? 'Hide' : 'View'} Orders
              </Button>
            </div>
          </div>
          {isDueAndPendingExpanded && (
            <MiniOrderTable
              orders={[...activeOrders, ...pendingOrders]}
              selectedOrderIds={
                totalBalanceType === 'dueAndPending' ? selectedOrderIds : []
              }
              setSelectedOrderIds={setSelectedOrderIds}
              title={
                hasPendingOrders && hasOpenOrders
                  ? 'Due and Upcoming Orders'
                  : 'Upcoming Orders'
              }
              tableName="dueAndPendingOrders"
            />
          )}
        </>
      )}
    </>
  );
};

export default TotalOrdersSelection;
